import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import animateScrollTo from "animated-scroll-to";
import { productsBySku } from "src/data/product-data";
import Container from "src/react/layout/container";
import { LinkButton } from "src/react/components/link";
import Breadcrumbs from "src/react/components/breadcrumbs";
import Currency from "src/react/components/currency";
import { color } from "src/styles/variables";
import ProductBasketButton from "src/react/pages/signup/components/product-basket-button";
import ProductStars from "./product-stars";
import ImageGallery from "./product-image-gallery";
import IntroTabs from "./intro-tabs";
import AdditionalInfo from "./additional-info";

function makeCrumbs(category, slug, title) {
  return [
    { path: `/products${slug ? `/#${slug}` : ""}`, text: "Products" },
    category
      ? {
          path: `/products/category/${category}`,
          text: category,
        }
      : null,
    { text: title },
  ].filter((crumb) => crumb);
}

function Intro({
  addToBasketLink,
  pendingLaunch,
  title,
  description,
  priceInPence,
  introCopy,
  slug,
  ctaLinkText,
  sku,
  isUserAccountActive,
  imageGallery,
  image,
  learnMoreTabText,
  learnMoreCopy,
  additionalInformationCtaText,
  additionalInformation,
  reviewsData,
  hideReviews,
}) {
  const productData = productsBySku[sku] || {};

  return (
    <Container>
      <Breadcrumbs crumbs={makeCrumbs(productData.category, slug, title)} />
      <Wrapper>
        <ImageGallery imageGallery={imageGallery} singleImage={image} />
        <ContentWrapper>
          <HeaderWrapper>
            <Header>
              <Title>{title}</Title>
              {pendingLaunch ? (
                <ComingSoon>COMING SOON</ComingSoon>
              ) : (
                <Price>
                  <Currency value={priceInPence} />
                </Price>
              )}
            </Header>
            {!hideReviews && (
              <StarsScrollLink
                title="Jump to top product reviews"
                onClick={(event) => {
                  event.preventDefault();
                  animateScrollTo(document.getElementById("reviews"));
                }}
              >
                <ProductStars
                  rating={parseFloat(reviewsData.stats.average || 0)}
                  reviewCount={reviewsData.stats.count || 0}
                />
              </StarsScrollLink>
            )}
            {description && <Description>{description}</Description>}
          </HeaderWrapper>
          {isUserAccountActive && (
            <LinkButton
              to={addToBasketLink || "/account/topup"}
              variant="primary"
              fullWidth
            >
              {ctaLinkText || "Place one-off order"}
            </LinkButton>
          )}
          {!pendingLaunch &&
            !isUserAccountActive &&
            (ctaLinkText ? (
              <LinkButton to={addToBasketLink} variant="primary" fullWidth>
                {ctaLinkText || "Get started"}
              </LinkButton>
            ) : (
              !!productData && (
                <ProductBasketButton
                  sku={sku}
                  isProductOutOfStock={productData.outOfStock}
                  fullWidth
                />
              )
            ))}
          <IntroTabs
            introCopy={introCopy}
            learnMoreTabText={learnMoreTabText}
            learnMoreCopy={learnMoreCopy}
          />
          {additionalInformationCtaText && additionalInformation && (
            <AdditionalInfo
              additionalInformationCtaText={additionalInformationCtaText}
              additionalInformation={additionalInformation}
            />
          )}
        </ContentWrapper>
      </Wrapper>
    </Container>
  );
}

Intro.propTypes = {
  title: PropTypes.string.isRequired,
  introCopy: PropTypes.string.isRequired,
  addToBasketLink: PropTypes.string,
  description: PropTypes.string,
  pendingLaunch: PropTypes.bool,
  isUserAccountActive: PropTypes.bool,
  sku: PropTypes.string,
  priceInPence: PropTypes.number,
  slug: PropTypes.string,
  imageGallery: PropTypes.array,
  image: PropTypes.object,
  ctaLinkText: PropTypes.string,
  reviewsData: PropTypes.object,
  learnMoreTabText: PropTypes.string,
  learnMoreCopy: PropTypes.string,
  additionalInformationCtaText: PropTypes.string,
  additionalInformation: PropTypes.string,
  hideReviews: PropTypes.bool,
};

export default Intro;

const mediumBreakpoint = "@media (min-width: 768px)";

const Wrapper = styled.div`
  margin-bottom: 40px;
  ${mediumBreakpoint} {
    display: flex;
  }
`;

const ContentWrapper = styled.div`
  flex: 1 1 auto;
  ${mediumBreakpoint} {
    margin-left: 30px;
  }
`;

const HeaderWrapper = styled.div`
  border-bottom: 1px solid ${color.dustyGrey};
  margin-bottom: 20px;
  padding-bottom: 5px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 25px;
  font-weight: 600;
  margin-right: 10px;
  ${mediumBreakpoint} {
    font-size: 28px;
  }
`;

const Price = styled.div`
  font-size: 24px;
`;

const Description = styled.p`
  margin-top: 10px;
`;

const ComingSoon = styled.div`
  color: ${color.blue};
  font-size: 16px;
  font-weight: 600;
`;

const StarsScrollLink = styled.a`
  p {
    border-bottom: 1px solid ${color.emperor};
    cursor: pointer;
    transition: border-bottom 0.2s linear;
    :hover {
      border-bottom-color: transparent;
    }
  }
`;
StarsScrollLink.displayName = "StarsScrollLink";
